import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, Package, PlusCircle, XCircle, Clock, BarChart3 } from 'lucide-react';
import * as d3 from 'd3';

const MarketSummary = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('autos');
  const [sortBy, setSortBy] = useState('active_stock');
  const barRef = useRef(null);
  const scatterRef = useRef(null);

  useEffect(() => { loadData(); }, [mode]);

  const loadData = async () => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const endpoint = mode === 'autos' ? '/market-summary' : '/motos/market-summary';
      const res = await fetch(`${apiUrl}${endpoint}`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const sorted = [...data].sort((a, b) => {
    if (sortBy === 'price_change_pct') return (b.price_change_pct || 0) - (a.price_change_pct || 0);
    if (sortBy === 'median_sell_time_days') return (a.median_sell_time_days || 99) - (b.median_sell_time_days || 99);
    return (b[sortBy] || 0) - (a[sortBy] || 0);
  });

  const formatPrice = (v) => v ? `US$ ${Math.round(v).toLocaleString('es-AR')}` : '-';
  const absorption = (d) => d.active_stock > 0 ? ((d.closed_count / d.active_stock) * 100).toFixed(1) : 0;

  // --- D3 BAR CHART ---
  const drawBarChart = useCallback(() => {
    if (!barRef.current || sorted.length === 0) return;
    const container = barRef.current;
    d3.select(container).selectAll('*').remove();

    const width = container.clientWidth;
    const height = container.clientHeight;
    const margin = { top: 10, right: 60, bottom: 10, left: 120 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const metrics = ['active_stock', 'new_listings', 'closed_count'];
    const metricLabels = { active_stock: 'Stock', new_listings: 'Nuevos', closed_count: 'Vendidos' };
    const colors = { active_stock: 'rgba(47,107,79,0.7)', new_listings: 'rgba(72,168,119,0.6)', closed_count: 'rgba(217,138,61,0.6)' };

    const segments = sorted.map(d => d.query?.replace(/-/g, ' ').toUpperCase());

    const y0 = d3.scaleBand().domain(segments).range([0, innerH]).paddingInner(0.25).paddingOuter(0.1);
    const y1 = d3.scaleBand().domain(metrics).range([0, y0.bandwidth()]).padding(0.08);
    const maxVal = d3.max(sorted, d => d3.max(metrics, m => d[m] || 0));
    const x = d3.scaleLinear().domain([0, maxVal * 1.15]).range([0, innerW]);

    const svg = d3.select(container).append('svg').attr('width', width).attr('height', height);
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Segment labels
    g.selectAll('.seg-label')
      .data(segments)
      .join('text')
      .attr('x', -8)
      .attr('y', d => y0(d) + y0.bandwidth() / 2)
      .attr('dy', '0.35em')
      .attr('text-anchor', 'end')
      .attr('fill', '#a3b18a')
      .attr('font-size', '11px')
      .attr('font-family', 'Inter, system-ui, sans-serif')
      .text(d => d);

    // Bars
    const segmentGroups = g.selectAll('.segment')
      .data(sorted)
      .join('g')
      .attr('transform', (d, i) => `translate(0,${y0(segments[i])})`);

    segmentGroups.selectAll('rect')
      .data(d => metrics.map(m => ({ metric: m, value: d[m] || 0 })))
      .join('rect')
      .attr('y', d => y1(d.metric))
      .attr('x', 0)
      .attr('height', y1.bandwidth())
      .attr('width', d => x(d.value))
      .attr('fill', d => colors[d.metric])
      .attr('rx', 2);

    // Value labels
    segmentGroups.selectAll('.val-label')
      .data(d => metrics.map(m => ({ metric: m, value: d[m] || 0 })))
      .join('text')
      .attr('x', d => x(d.value) + 5)
      .attr('y', d => y1(d.metric) + y1.bandwidth() / 2)
      .attr('dy', '0.35em')
      .attr('fill', '#d1d5db')
      .attr('font-size', '10px')
      .attr('font-variant-numeric', 'tabular-nums')
      .text(d => d.value.toLocaleString());

    // Legend (top-right, minimal)
    const legend = svg.append('g').attr('transform', `translate(${margin.left + innerW - 180}, ${margin.top})`);
    metrics.forEach((m, i) => {
      legend.append('rect').attr('x', i * 65).attr('y', 0).attr('width', 10).attr('height', 10).attr('fill', colors[m]).attr('rx', 2);
      legend.append('text').attr('x', i * 65 + 14).attr('y', 9).attr('fill', '#a3b18a').attr('font-size', '10px').text(metricLabels[m]);
    });
  }, [sorted]);

  // --- D3 SCATTER/BUBBLE CHART ---
  const drawScatterChart = useCallback(() => {
    if (!scatterRef.current || sorted.length === 0) return;
    const container = scatterRef.current;
    d3.select(container).selectAll('*').remove();

    const width = container.clientWidth;
    const height = container.clientHeight;
    const margin = { top: 10, right: 20, bottom: 40, left: 50 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const validData = sorted.filter(d => d.median_price_usd && d.median_sell_time_days);
    if (validData.length === 0) return;

    const x = d3.scaleLinear()
      .domain([0, d3.max(validData, d => d.median_price_usd) * 1.1])
      .range([0, innerW]);
    const y = d3.scaleLinear()
      .domain([0, d3.max(validData, d => d.median_sell_time_days) * 1.15])
      .range([innerH, 0]);
    const r = d3.scaleSqrt()
      .domain([0, d3.max(validData, d => d.active_stock)])
      .range([4, 28]);

    const svg = d3.select(container).append('svg').attr('width', width).attr('height', height);
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Grid
    g.append('g').attr('class', 'grid-x')
      .attr('transform', `translate(0,${innerH})`)
      .call(d3.axisBottom(x).ticks(5).tickSize(-innerH).tickFormat(''))
      .selectAll('line').attr('stroke', 'rgba(156,163,175,0.15)');
    g.select('.grid-x .domain').remove();

    g.append('g').attr('class', 'grid-y')
      .call(d3.axisLeft(y).ticks(5).tickSize(-innerW).tickFormat(''))
      .selectAll('line').attr('stroke', 'rgba(156,163,175,0.15)');
    g.select('.grid-y .domain').remove();

    // Axes
    const formatK = v => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`;
    g.append('g').attr('transform', `translate(0,${innerH})`)
      .call(d3.axisBottom(x).ticks(5).tickFormat(formatK))
      .selectAll('text').attr('fill', '#a3b18a').attr('font-size', '10px');
    g.select('g:last-of-type .domain').attr('stroke', 'rgba(156,163,175,0.3)');

    g.append('g')
      .call(d3.axisLeft(y).ticks(5))
      .selectAll('text').attr('fill', '#a3b18a').attr('font-size', '10px');
    g.selectAll('.domain').attr('stroke', 'rgba(156,163,175,0.3)');

    // Axis labels
    g.append('text').attr('x', innerW / 2).attr('y', innerH + 32).attr('text-anchor', 'middle')
      .attr('fill', '#6b7280').attr('font-size', '11px').text('Precio Mediano (USD)');
    g.append('text').attr('transform', 'rotate(-90)').attr('x', -innerH / 2).attr('y', -38).attr('text-anchor', 'middle')
      .attr('fill', '#6b7280').attr('font-size', '11px').text('Días para vender');

    // Bubbles
    const bubbles = g.selectAll('circle')
      .data(validData)
      .join('circle')
      .attr('cx', d => x(d.median_price_usd))
      .attr('cy', d => y(d.median_sell_time_days))
      .attr('r', d => r(d.active_stock))
      .attr('fill', 'rgba(156,163,175,0.45)')
      .attr('stroke', 'rgba(156,163,175,0.6)')
      .attr('stroke-width', 1)
      .style('cursor', 'pointer');

    // Direct labels on top 4 largest
    const topN = [...validData].sort((a, b) => b.active_stock - a.active_stock).slice(0, 4);
    g.selectAll('.direct-label')
      .data(topN)
      .join('text')
      .attr('x', d => x(d.median_price_usd))
      .attr('y', d => y(d.median_sell_time_days) - r(d.active_stock) - 5)
      .attr('text-anchor', 'middle')
      .attr('fill', '#d1d5db')
      .attr('font-size', '9px')
      .attr('font-weight', '500')
      .text(d => d.query?.replace(/-/g, ' '));

    // Voronoi hover
    const delaunay = d3.Delaunay.from(validData, d => x(d.median_price_usd), d => y(d.median_sell_time_days));

    const tooltip = d3.select(container).append('div')
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('background', 'rgba(17,24,39,0.95)')
      .style('border', '1px solid rgba(47,107,79,0.4)')
      .style('border-radius', '8px')
      .style('padding', '8px 12px')
      .style('font-size', '11px')
      .style('color', '#e5e7eb')
      .style('opacity', 0)
      .style('transition', 'opacity 0.15s, left 0.1s, top 0.1s')
      .style('white-space', 'nowrap')
      .style('z-index', 10);

    g.append('rect')
      .attr('width', innerW).attr('height', innerH).attr('fill', 'transparent')
      .on('mousemove', function (event) {
        const [mx, my] = d3.pointer(event);
        const idx = delaunay.find(mx, my);
        if (idx === -1) return;
        const d = validData[idx];

        bubbles.attr('fill', 'rgba(156,163,175,0.45)').attr('stroke', 'rgba(156,163,175,0.6)');
        bubbles.filter((_, i) => i === idx)
          .attr('fill', 'rgba(47,107,79,0.8)')
          .attr('stroke', 'rgba(129,140,248,1)');

        tooltip
          .style('opacity', 1)
          .style('left', `${x(d.median_price_usd) + margin.left + 15}px`)
          .style('top', `${y(d.median_sell_time_days) + margin.top - 10}px`)
          .html(`
            <div style="font-weight:600;margin-bottom:3px;color:#a5b4fc">${d.query?.replace(/-/g, ' ').toUpperCase()}</div>
            <div>Precio: <b>${formatPrice(d.median_price_usd)}</b></div>
            <div>Venta: <b>${Math.round(d.median_sell_time_days)} días</b></div>
            <div>Stock: <b>${d.active_stock}</b></div>
            <div>Absorción: <b>${absorption(d)}%</b></div>
          `);
      })
      .on('mouseleave', function () {
        bubbles.attr('fill', 'rgba(156,163,175,0.45)').attr('stroke', 'rgba(156,163,175,0.6)');
        tooltip.style('opacity', 0);
      });
  }, [sorted]);

  // Draw charts when data changes
  useEffect(() => {
    if (!loading && sorted.length > 0) {
      drawBarChart();
      drawScatterChart();
    }
  }, [loading, sorted, drawBarChart, drawScatterChart]);

  // Responsive resize
  useEffect(() => {
    const handleResize = () => { drawBarChart(); drawScatterChart(); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawBarChart, drawScatterChart]);

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center space-x-3">
          <BarChart3 className="w-6 h-6 text-[#48a877]" />
          <h2 className="text-xl font-bold text-white">Resumen Semanal</h2>
          {data[0]?.week_start && <span className="text-xs text-gray-400">Semana del {data[0].week_start}</span>}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-[#1c1c1a] border border-gray-700 rounded-lg overflow-hidden">
            {['autos', 'motos'].map(m => (
              <button key={m} onClick={() => setMode(m)}
                className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${mode === m ? 'bg-[#2f6b4f] text-white' : 'text-gray-400 hover:text-white'}`}
              >{m}</button>
            ))}
          </div>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="px-3 py-2 bg-[#1c1c1a] border border-gray-700 rounded-lg text-sm text-white">
            <option value="active_stock">Stock</option>
            <option value="price_change_pct">Var. Precio</option>
            <option value="median_sell_time_days">Tiempo Venta</option>
            <option value="closed_count">Vendidos</option>
          </select>
        </div>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
        {sorted.map(d => (
          <div key={d.query} className="bg-[#1c1c1a] border border-[#2a2926] rounded-xl p-4 hover:border-gray-600 transition-colors">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-white uppercase tracking-wide">{d.query?.replace(/-/g, ' ')}</h3>
              <PriceChangeIndicator pct={d.price_change_pct} />
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
              <div>
                <div className="flex items-center justify-center space-x-1 text-blue-400">
                  <Package className="w-3 h-3" />
                  <span className="text-lg font-bold">{d.active_stock}</span>
                </div>
                <p className="text-[10px] text-gray-500">Stock</p>
              </div>
              <div>
                <div className="flex items-center justify-center space-x-1 text-green-400">
                  <PlusCircle className="w-3 h-3" />
                  <span className="text-lg font-bold">{d.new_listings}</span>
                </div>
                <p className="text-[10px] text-gray-500">Nuevos</p>
              </div>
              <div>
                <div className="flex items-center justify-center space-x-1 text-orange-400">
                  <XCircle className="w-3 h-3" />
                  <span className="text-lg font-bold">{d.closed_count}</span>
                </div>
                <p className="text-[10px] text-gray-500">Vendidos</p>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-400">Precio mediano</span>
              <span className="text-white font-semibold">{formatPrice(d.median_price_usd)}</span>
            </div>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />Venta</span>
              <span className={`font-semibold ${d.median_sell_time_days <= 20 ? 'text-green-400' : d.median_sell_time_days >= 40 ? 'text-red-400' : 'text-yellow-400'}`}>
                {d.median_sell_time_days ? `${Math.round(d.median_sell_time_days)} días` : '-'}
              </span>
            </div>
            <div className="mt-2">
              <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                <span>Absorción</span>
                <span>{absorption(d)}%</span>
              </div>
              <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${parseFloat(absorption(d)) > 10 ? 'bg-green-400' : parseFloat(absorption(d)) < 3 ? 'bg-red-400' : 'bg-yellow-400'}`}
                  style={{ width: `${Math.min(100, parseFloat(absorption(d)) * 3)}%` }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-effect rounded-xl p-4 lg:p-6">
          <h3 className="text-sm font-semibold text-white mb-4">Stock vs Flujo por Segmento</h3>
          <div ref={barRef} className="h-64 lg:h-80 relative" />
        </div>
        <div className="glass-effect rounded-xl p-4 lg:p-6">
          <h3 className="text-sm font-semibold text-white mb-1">Liquidez vs Precio</h3>
          <p className="text-[10px] text-gray-500 mb-4">Tamaño = stock · Gris = todos los segmentos</p>
          <div ref={scatterRef} className="h-64 lg:h-80 relative" />
        </div>
      </div>
    </div>
  );
};

const PriceChangeIndicator = ({ pct }) => {
  if (!pct || pct === 0) return <Minus className="w-4 h-4 text-gray-500" />;
  if (pct > 0) return (
    <div className="flex items-center space-x-1 text-green-400">
      <TrendingUp className="w-3.5 h-3.5" />
      <span className="text-xs font-bold">+{pct.toFixed(1)}%</span>
    </div>
  );
  return (
    <div className="flex items-center space-x-1 text-red-400">
      <TrendingDown className="w-3.5 h-3.5" />
      <span className="text-xs font-bold">{pct.toFixed(1)}%</span>
    </div>
  );
};

export default MarketSummary;
